import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import VariableBadge from '../VariableBadge.vue';

export type VariableType = 'system' | 'slot' | 'fragment';

export interface VariableNodeAttributes {
  type: VariableType;
  name: string;
  preview?: string;
  /** Preserved from {{slot:name | default: "..."}} for round-trip serialization */
  defaultValue?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableNode: {
      insertVariable: (attributes: VariableNodeAttributes) => ReturnType;
    };
  }
}

export const VariableNode = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true, // Cannot be edited directly

  addAttributes() {
    return {
      type: {
        default: 'system',
        parseHTML: (element) => element.getAttribute('data-type'),
        renderHTML: (attributes) => ({
          'data-type': attributes.type,
        }),
      },
      name: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-name'),
        renderHTML: (attributes) => ({
          'data-name': attributes.name,
        }),
      },
      preview: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-preview'),
        renderHTML: (attributes) => ({
          'data-preview': attributes.preview,
        }),
      },
      defaultValue: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-default-value'),
        renderHTML: (attributes) =>
          attributes.defaultValue
            ? { 'data-default-value': attributes.defaultValue }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-variable': '' }),
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(VariableBadge);
  },

  addCommands() {
    return {
      insertVariable:
        (attributes: VariableNodeAttributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
    };
  },
});

type InlineNode =
  | { type: 'text'; text: string; marks?: { type: string }[] }
  | { type: 'variable'; attrs: VariableNodeAttributes };

/**
 * Parse inline content (text with bold, italic, code, and variables)
 */
function parseInlineContent(
  text: string,
  getPreview?: (type: VariableType, name: string) => string | undefined,
): InlineNode[] {
  const result: InlineNode[] = [];

  // Combined pattern for variables and inline formatting
  // Order matters: check longer patterns first
  const patterns = [
    // Variables: {{fragment:name}}, {{slot:name}}, {{slot:name | default: "..."}}, {{name}}
    // Defaults are a slot-only concept (backend syntax); a fragment with a
    // default must not match, so it stays literal text instead of having its
    // default silently dropped on serialize.
    {
      regex:
        /\{\{(slot):([a-z0-9_-]+)(?:\s*\|\s*default\s*:\s*(?:"([^"]*)"|'([^']*)'))?\}\}/gi,
      type: 'variable-typed' as const,
    },
    {
      regex: /\{\{(fragment):([a-z0-9_-]+)\}\}/gi,
      type: 'variable-typed' as const,
    },
    { regex: /\{\{([a-z0-9_]+)\}\}/gi, type: 'variable-system' as const },
    // Bold: **text**
    { regex: /\*\*([^*]+)\*\*/g, type: 'bold' as const },
    // Italic: *text* (but not **)
    { regex: /(?<!\*)\*([^*]+)\*(?!\*)/g, type: 'italic' as const },
    // Code: `text`
    { regex: /`([^`]+)`/g, type: 'code' as const },
  ];

  // Find all matches with their positions
  interface Match {
    index: number;
    length: number;
    type: string;
    content: string;
    varType?: VariableType;
    varName?: string;
    defaultValue?: string;
  }

  const matches: Match[] = [];

  for (const { regex, type } of patterns) {
    regex.lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (type === 'variable-typed') {
        const defaultValue = match[3] ?? match[4];
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'variable',
          content: match[0],
          varType: match[1].toLowerCase() as 'fragment' | 'slot',
          varName: match[2],
          ...(defaultValue !== undefined ? { defaultValue } : {}),
        });
      } else if (type === 'variable-system') {
        matches.push({
          index: match.index,
          length: match[0].length,
          type: 'variable',
          content: match[0],
          varType: 'system',
          varName: match[1],
        });
      } else {
        matches.push({
          index: match.index,
          length: match[0].length,
          type,
          content: match[1],
        });
      }
    }
  }

  // Sort by position and filter overlapping matches
  matches.sort((a, b) => a.index - b.index);
  const filteredMatches: Match[] = [];
  let lastEnd = 0;
  for (const m of matches) {
    if (m.index >= lastEnd) {
      filteredMatches.push(m);
      lastEnd = m.index + m.length;
    }
  }

  // Build result
  let pos = 0;
  for (const m of filteredMatches) {
    // Add plain text before this match
    if (m.index > pos) {
      result.push({ type: 'text', text: text.slice(pos, m.index) });
    }

    if (m.type === 'variable' && m.varType && m.varName) {
      const preview = getPreview?.(m.varType, m.varName);
      result.push({
        type: 'variable',
        attrs: {
          type: m.varType,
          name: m.varName,
          preview,
          ...(m.defaultValue !== undefined
            ? { defaultValue: m.defaultValue }
            : {}),
        },
      });
    } else if (m.type === 'bold') {
      result.push({ type: 'text', text: m.content, marks: [{ type: 'bold' }] });
    } else if (m.type === 'italic') {
      result.push({ type: 'text', text: m.content, marks: [{ type: 'italic' }] });
    } else if (m.type === 'code') {
      result.push({ type: 'text', text: m.content, marks: [{ type: 'code' }] });
    }

    pos = m.index + m.length;
  }

  // Add remaining text
  if (pos < text.length) {
    result.push({ type: 'text', text: text.slice(pos) });
  }

  return result;
}

/**
 * Convert raw markdown text with {{...}} syntax to TipTap-compatible JSON content
 */
export function parseMarkdownToContent(
  text: string,
  getPreview?: (type: VariableType, name: string) => string | undefined,
): { type: string; content: unknown[] } {
  const lines = text.split('\n');
  const content: unknown[] = [];

  interface ListLineMeta {
    indent: number;
    ordered: boolean;
    start: number;
    text: string;
  }

  const getListLineMeta = (line: string): ListLineMeta | null => {
    const match = line.match(/^(\s*)([-*]|\d+\.)\s+(.+)$/);
    if (!match) return null;

    const marker = match[2];
    const ordered = /\d+\./.test(marker);
    return {
      indent: match[1].length,
      ordered,
      start: ordered ? parseInt(marker, 10) : 1,
      text: match[3],
    };
  };

  const parseListAt = (
    startIndex: number,
    baseIndent: number,
  ): { node: { type: string; attrs?: { start: number }; content: unknown[] }; nextIndex: number } => {
    const firstMeta = getListLineMeta(lines[startIndex]);
    if (!firstMeta) {
      return { node: { type: 'bulletList', content: [] }, nextIndex: startIndex };
    }

    const ordered = firstMeta.ordered;
    const listStart = firstMeta.start;
    const items: unknown[] = [];
    let i = startIndex;

    while (i < lines.length) {
      const currentMeta = getListLineMeta(lines[i]);
      if (!currentMeta) break;
      if (currentMeta.indent < baseIndent) break;
      if (currentMeta.indent > baseIndent) {
        if (items.length === 0) break;
        const lastItem = items[items.length - 1] as {
          type: 'listItem';
          content: unknown[];
        };
        const nested = parseListAt(i, currentMeta.indent);
        if (nested.node.content.length > 0) {
          lastItem.content.push(nested.node);
        }
        i = nested.nextIndex;
        continue;
      }

      if (currentMeta.ordered !== ordered) break;

      const item: { type: 'listItem'; content: unknown[] } = {
        type: 'listItem',
        content: [
          {
            type: 'paragraph',
            content: parseInlineContent(currentMeta.text, getPreview),
          },
        ],
      };
      i++;

      while (i < lines.length) {
        const nextLine = lines[i];
        if (nextLine.trim() === '') {
          i++;
          continue;
        }

        const nextMeta = getListLineMeta(nextLine);
        if (nextMeta) {
          if (nextMeta.indent > baseIndent) {
            const nested = parseListAt(i, nextMeta.indent);
            if (nested.node.content.length > 0) {
              item.content.push(nested.node);
            }
            i = nested.nextIndex;
            continue;
          }

          if (nextMeta.indent <= baseIndent) {
            break;
          }
        }

        const leadingIndent = (nextLine.match(/^(\s*)/)?.[1].length ?? 0);
        if (leadingIndent > baseIndent) {
          item.content.push({
            type: 'paragraph',
            content: parseInlineContent(nextLine.trim(), getPreview),
          });
          i++;
          continue;
        }

        break;
      }

      items.push(item);
    }

    const node =
      ordered
        ? {
            type: 'orderedList',
            attrs: { start: listStart },
            content: items,
          }
        : {
            type: 'bulletList',
            content: items,
          };

    return { node, nextIndex: i };
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Heading patterns
    const h1Match = line.match(/^# (.+)$/);
    const h2Match = line.match(/^## (.+)$/);
    const h3Match = line.match(/^### (.+)$/);

    if (h3Match) {
      content.push({
        type: 'heading',
        attrs: { level: 3 },
        content: parseInlineContent(h3Match[1], getPreview),
      });
      i++;
      continue;
    }

    if (h2Match) {
      content.push({
        type: 'heading',
        attrs: { level: 2 },
        content: parseInlineContent(h2Match[1], getPreview),
      });
      i++;
      continue;
    }

    if (h1Match) {
      content.push({
        type: 'heading',
        attrs: { level: 1 },
        content: parseInlineContent(h1Match[1], getPreview),
      });
      i++;
      continue;
    }

    // Lists (supports nesting and ordered-list start values)
    const listMeta = getListLineMeta(line);
    if (listMeta) {
      const parsed = parseListAt(i, listMeta.indent);
      if (parsed.node.content.length > 0) {
        content.push(parsed.node);
      }
      i = parsed.nextIndex;
      continue;
    }

    // Regular paragraph (or empty line)
    if (line.trim() === '') {
      // Skip empty lines between blocks, but add empty paragraph if needed
      if (content.length > 0) {
        const lastNode = content[content.length - 1] as { type: string };
        if (lastNode.type === 'paragraph') {
          // Don't add extra empty paragraphs
        }
      }
    } else {
      const inlineContent = parseInlineContent(line, getPreview);
      if (inlineContent.length > 0) {
        content.push({ type: 'paragraph', content: inlineContent });
      } else {
        content.push({ type: 'paragraph' });
      }
    }
    i++;
  }

  // Ensure at least one paragraph
  if (content.length === 0) {
    content.push({ type: 'paragraph' });
  }

  return { type: 'doc', content };
}

/**
 * Simple inline-only parsing for backwards compatibility
 */
export function parseVariableSyntax(
  text: string,
  getPreview?: (type: VariableType, name: string) => string | undefined,
): InlineNode[] {
  return parseInlineContent(text, getPreview);
}

/**
 * Serialize TipTap content back to raw text with {{...}} syntax and markdown
 */
export function serializeToSyntax(json: { type: string; content?: unknown[] }): string {
  if (!json.content) return '';

  const lines: string[] = [];

  interface MarkType {
    type: string;
  }

  interface NodeType {
    type: string;
    text?: string;
    attrs?: VariableNodeAttributes & { level?: number; start?: number };
    content?: NodeType[];
    marks?: MarkType[];
  }

  const wrapWithMarks = (text: string, marks?: MarkType[]): string => {
    if (!marks || marks.length === 0) return text;

    let wrapped = text;
    for (const mark of marks) {
      switch (mark.type) {
        case 'bold':
          wrapped = `**${wrapped}**`;
          break;
        case 'italic':
          wrapped = `*${wrapped}*`;
          break;
        case 'code':
          wrapped = `\`${wrapped}\``;
          break;
      }
    }
    return wrapped;
  };

  const processInlineContent = (content?: NodeType[]): string => {
    if (!content) return '';
    let text = '';
    for (const node of content) {
      if (node.type === 'text' && node.text) {
        text += wrapWithMarks(node.text, node.marks);
      } else if (node.type === 'variable' && node.attrs) {
        const { type, name, defaultValue } = node.attrs;
        if (type === 'system') {
          text += `{{${name}}}`;
        } else if (type === 'slot' && defaultValue != null) {
          const value = String(defaultValue);
          const quoted = value.includes('"')
            ? `'${value.replace(/'/g, '')}'`
            : `"${value}"`;
          text += `{{${type}:${name} | default: ${quoted}}}`;
        } else {
          text += `{{${type}:${name}}}`;
        }
      } else if (node.type === 'hardBreak') {
        text += '\n';
      }
    }
    return text;
  };

  const processListItems = (
    items: NodeType[],
    ordered: boolean,
    depth = 0,
    start = 1,
  ): void => {
    const indent = '  '.repeat(depth);
    items.forEach((item, index) => {
      if (item.type === 'listItem' && item.content) {
        let wrotePrimaryLine = false;
        item.content.forEach((child) => {
          if (child.type === 'paragraph') {
            const paragraphText = processInlineContent(child.content);
            if (!wrotePrimaryLine) {
              const prefix = ordered ? `${start + index}. ` : '- ';
              lines.push(`${indent}${prefix}${paragraphText}`);
              wrotePrimaryLine = true;
            } else {
              lines.push(`${indent}  ${paragraphText}`);
            }
          } else if (child.type === 'bulletList' && child.content) {
            if (!wrotePrimaryLine) {
              const prefix = ordered ? `${start + index}. ` : '- ';
              lines.push(`${indent}${prefix}`);
              wrotePrimaryLine = true;
            }
            processListItems(child.content, false, depth + 1);
          } else if (child.type === 'orderedList' && child.content) {
            if (!wrotePrimaryLine) {
              const prefix = ordered ? `${start + index}. ` : '- ';
              lines.push(`${indent}${prefix}`);
              wrotePrimaryLine = true;
            }
            processListItems(
              child.content,
              true,
              depth + 1,
              typeof child.attrs?.start === 'number' ? child.attrs.start : 1,
            );
          }
        });
      }
    });
  };

  const processNode = (node: NodeType): void => {
    switch (node.type) {
      case 'paragraph':
        lines.push(processInlineContent(node.content));
        break;
      case 'heading':
        const level = node.attrs?.level || 1;
        const prefix = '#'.repeat(level) + ' ';
        lines.push(prefix + processInlineContent(node.content));
        break;
      case 'bulletList':
        if (node.content) {
          processListItems(node.content, false);
        }
        break;
      case 'orderedList':
        if (node.content) {
          processListItems(
            node.content,
            true,
            0,
            typeof node.attrs?.start === 'number' ? node.attrs.start : 1,
          );
        }
        break;
      case 'hardBreak':
        lines.push('');
        break;
      default:
        if (node.content) {
          node.content.forEach(processNode);
        }
    }
  };

  (json.content as NodeType[]).forEach(processNode);

  return lines.join('\n');
}
